import React, { useState } from 'react';

interface RouteStep {
  id: string;
  sequence: number;
  process: string;
  machineName?: string | null;
  estimatedDurationMin?: number | null;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
}

interface Props {
  orderId: string;
  routes: RouteStep[];
  onRecalculated: () => void;
}

const fmt = (v?: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const fmtDuration = (min?: number | null) => {
  if (!min) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
};

// Read-only view of the planner's per-process schedule, with a button to recalculate it.
const RoutePlanningSummary: React.FC<Props> = ({ orderId, routes, onRecalculated }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const planned = routes.some((r) => r.estimatedDurationMin || r.plannedStartAt);

  const recalc = async () => {
    setBusy(true);
    setError(null);
    setNotes([]);
    try {
      const res = await fetch('/api/gantt/plan-route', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ orderId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || 'Error al calcular la programación por proceso');
      }
      const body = await res.json().catch(() => ({}));
      setNotes(
        (Array.isArray(body?.steps) ? body.steps : []).flatMap((st: any) =>
          (st.notes || []).filter((n: string) => n !== 'No requiere máquina').map((n: string) => `${st.process}: ${n}`)),
      );
      onRecalculated();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (routes.length === 0) return null;

  return (
    <div className="mt-4 border rounded-lg p-4 bg-gray-50">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h4 className="text-sm font-semibold text-gray-700">Programación por proceso</h4>
        <button
          type="button"
          disabled={busy}
          onClick={recalc}
          className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm disabled:opacity-50"
        >
          {busy ? 'Calculando...' : planned ? 'Recalcular' : 'Calcular'}
        </button>
      </div>
      {error && <p className="text-sm text-red-700 mb-2">{error}</p>}
      {notes.length > 0 && (
        <ul className="list-disc list-inside text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2 space-y-0.5">
          {notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
      {!planned ? (
        <p className="text-sm text-gray-500">
          Aún no se calcula. Usa «Calcular» (parte de la fecha de inicio planeada, o de ahora si no tiene) o aplica una simulación.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-gray-600">
              <tr>
                <th className="px-2 py-1 font-semibold">#</th>
                <th className="px-2 py-1 font-semibold">Proceso</th>
                <th className="px-2 py-1 font-semibold">Máquina</th>
                <th className="px-2 py-1 font-semibold">Duración</th>
                <th className="px-2 py-1 font-semibold">Inicio</th>
                <th className="px-2 py-1 font-semibold">Fin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {routes.map((r) => (
                <tr key={r.id}>
                  <td className="px-2 py-1">{r.sequence}</td>
                  <td className="px-2 py-1">{r.process}</td>
                  <td className="px-2 py-1">{r.machineName || '—'}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{r.estimatedDurationMin ? fmtDuration(r.estimatedDurationMin) : 'No usa máquina'}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{fmt(r.plannedStartAt)}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{fmt(r.plannedEndAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-500 mt-2">
            Los procesos se programan uno tras otro dentro del horario de planta; la espera de curado corre las 24 horas. Si faltan datos en la orden o en el equipo
            (gramaje, ancho de sustrato, velocidad), ese proceso usa 60 min.
          </p>
        </div>
      )}
    </div>
  );
};

export default RoutePlanningSummary;
